<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import {
  PBtnMenu,
  PCard,
  PCardSize,
  PDialogPosition,
  PItem,
  PList,
  PMenu,
  PModalDialog,
  PSkeleton,
  PSpace,
  PSpaceSize,
  PTheme,
  usePrevioDS,
} from '@previo/design-system'
import { useRoute, useRouter } from 'vue-router'
import { useUserStore } from '~/store/user'
import { usePropertyStore } from '~/store/property/property'
import { usePropertiesStore } from '~/store/property/properties'
import SwitchProperty from '~/components/switch-property/SwitchProperty.vue'

const userStore = useUserStore()
const selectedPropertyStore = usePropertyStore()
const propertiesStore = usePropertiesStore()
const router = useRouter()
const route = useRoute()
const ds = usePrevioDS()
const showPropertySelectInMenu = ref<boolean>(false)
const showPropertySelectInDialog = ref<boolean>(false)
const showPropertySelectInMobileSuggester = ref<boolean>(false)

const userHasLotsOfProperties = computed<boolean>(() => {
  return propertiesStore.properties.length > 15
})

const userHasSinglePropertyAccess = computed<boolean>(() => {
  return propertiesStore.properties.length === 1
})

const btnIcon = computed<string>(() => {
  return userHasSinglePropertyAccess.value ? '' : 'chevron-down'
})

const onHotelSelectClick = (): void => {
  if (userHasSinglePropertyAccess.value) {
    return
  }

  if (userHasLotsOfProperties.value || userStore.isRoot()) {
    if (ds.screen().isMobile) {
      showPropertySelectInMobileSuggester.value = true
      return
    }

    showPropertySelectInDialog.value = true
    return
  }

  showPropertySelectInMenu.value = !showPropertySelectInMenu.value
}

const onPropertySelectClick = (propertyId: number): void => {
  selectedPropertyStore.lastSelected = propertyId

  router
    .push({
      name: route.name ?? undefined,
      params: { propertyId: propertyId },
    })
    .finally(() => {
      window.location.reload()
    })
}

onMounted(() => {
  propertiesStore.loadProperties()
})
</script>

<template>
  <p-btn-menu
    :text="selectedPropertyStore.property.name"
    :theme="PTheme.DARK"
    outline
    :icon-end="btnIcon"
    cy="switch-hotel"
    highlighted
    @click="onHotelSelectClick"
  >
    <p-menu
      v-model="showPropertySelectInMenu"
      anchor="bottom end"
      self="top end"
      cy="property"
      no-parent-event
    >
      <p-card :pa-mobile="PCardSize.ZERO">
        <template v-if="propertiesStore.loading">
          <p-list>
            <p-item v-for="i in 3" :key="i" text="" cy="property-loading-skeleton">
              <p-skeleton height="100%" width="200px" />
            </p-item>
          </p-list>
        </template>

        <p-list v-else>
          <p-item
            v-for="property in propertiesStore.properties"
            :key="property.id"
            :text="property.name"
            :cy="`select-hotel-${property.id}`"
            @click="onPropertySelectClick(property.id)"
          />
        </p-list>
      </p-card>
    </p-menu>
  </p-btn-menu>

  <p-modal-dialog
    v-model="showPropertySelectInDialog"
    :title="$t('Select property')"
    :position="PDialogPosition.TOP"
    cy="select-property"
  >
    <switch-property @selected-property="onPropertySelectClick" />
    <p-space :mobile="PSpaceSize.MD" vertical />
  </p-modal-dialog>

  <div v-if="showPropertySelectInMobileSuggester" v-show="false">
    <switch-property
      @selected-property="onPropertySelectClick"
      @dismiss="showPropertySelectInMobileSuggester = false"
    />
  </div>
</template>
